/**
 * Sol menünün kullanıcıya özel bölümleri — Dashboard'lar, AI Raporları, Favoriler — üst
 * menü ve dar şerit için tek listede.
 *
 * Üst menü yalnız modül ağacını gösteriyordu; menüyü "Üstte" seçen kullanıcı dashboard'larına
 * ulaşamıyordu (sol menüde bölüm olarak vardı). Boş bölüm hiç çıkmaz: kurulumlar birbirine
 * benzemiyor, boş bir "Dashboard'lar" düğmesi yanıltırdı.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Sparkles, Star, type LucideIcon } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/shared/ui/DropdownMenu';
import { useTabsStore } from '../tabs/tabsStore';
import { useOpenScreen } from '../tabs/useOpenScreen';
import { useMenu } from './useMenu';
import { NavIcon } from './NavIcon';
import { useDashboards } from './DashboardsGroup';
import { useAiReportsMenu } from './AiReportsGroup';
import type { ReactNode } from 'react';

export interface NavGroupItem {
  id: string;
  caption: string;
  /** İpucu (ör. parametreli AI raporu). */
  title?: string;
  icon: ReactNode;
  active: boolean;
  open: () => void;
}

export interface NavGroup {
  id: 'dashboards' | 'aiReports' | 'favorites';
  caption: string;
  Icon: LucideIcon;
  items: NavGroupItem[];
  /** Açık sekme bu bölümden mi — düğme vurgulanır. */
  active: boolean;
}

export function useNavGroups(): NavGroup[] {
  const { t } = useTranslation();
  const dashboards = useDashboards();
  const aiReports = useAiReportsMenu();
  const { favorites } = useMenu();
  const openCustom = useTabsStore((s) => s.openCustom);
  const openScreen = useOpenScreen();
  const activeTab = useTabsStore((s) => s.tabs.find((x) => x.tabId === s.activeTabId));
  const activeKey = activeTab?.uniqueKey ?? null;
  const activeScreenId = activeTab?.screenId ?? null;

  return useMemo(() => {
    const groups: NavGroup[] = [];
    const push = (g: Omit<NavGroup, 'active'>) => {
      if (g.items.length) groups.push({ ...g, active: g.items.some((i) => i.active) });
    };
    push({
      id: 'dashboards',
      caption: t('shell.dashboards'),
      Icon: BarChart3,
      items: dashboards.map((d) => ({
        id: d.dashboardUserId,
        caption: d.dashboard!.dashboardName,
        icon: <BarChart3 className="text-brand-600 h-4 w-4 shrink-0" />,
        active: activeKey === `dashboard:${d.dashboardId}`,
        open: () =>
          openCustom({
            kind: 'dashboard',
            caption: d.dashboard!.dashboardName,
            params: { dashboardId: d.dashboardId, refreshIntervalSec: d.dashboard!.refreshIntervalSec ?? 0 },
            uniqueKey: `dashboard:${d.dashboardId}`,
          }),
      })),
    });
    push({
      id: 'aiReports',
      caption: t('shell.aiReports'),
      Icon: Sparkles,
      items: aiReports.map((r) => ({
        id: r.reportId,
        caption: r.title,
        title: r.hasParameters ? t('ai.artifact.parametric') : undefined,
        icon: <Sparkles className="text-brand-600 h-4 w-4 shrink-0" />,
        active: activeKey === `aireport:${r.reportId}`,
        open: () =>
          openCustom({
            kind: 'aireport',
            caption: r.title,
            params: { reportId: r.reportId },
            uniqueKey: `aireport:${r.reportId}`,
          }),
      })),
    });
    push({
      id: 'favorites',
      caption: t('shell.favorites'),
      Icon: Star,
      items: favorites.map((n) => ({
        id: n.screenId,
        caption: n.caption,
        icon: <NavIcon node={n} className="h-4 w-4 shrink-0" />,
        active: n.screenId === activeScreenId,
        open: () => void openScreen(n),
      })),
    });
    return groups;
  }, [t, dashboards, aiReports, favorites, openCustom, openScreen, activeKey, activeScreenId]);
}

/** Bölümün öğeleri — açılır menü içinde. */
export function NavGroupItems({ group }: { group: NavGroup }) {
  return (
    <>
      {group.items.map((i) => (
        <DropdownMenuItem
          key={i.id}
          onSelect={i.open}
          title={i.title}
          className={cn(i.active && 'bg-brand-500/10 text-brand-700 dark:text-brand-400')}
        >
          {i.icon}
          <span className="min-w-0 flex-1 truncate">{i.caption}</span>
        </DropdownMenuItem>
      ))}
    </>
  );
}

/** Bölüm, bir üst menünün ("…") alt menüsü olarak. */
export function NavGroupSub({ group }: { group: NavGroup }) {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger
        className={cn('font-semibold', group.active && 'text-brand-700 dark:text-brand-400')}
      >
        <group.Icon className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate">{group.caption}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="max-h-[80vh] w-64 overflow-y-auto">
        <NavGroupItems group={group} />
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
